import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Presentation } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import AccessDenied from "components/AccessDenied";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import NotFoundItem from "routes/404/NotFoundItem";
import { RecordLoadError } from "routes/swoogo/overviewPrimitives";
import { swoogoSessionKeys } from "routes/swoogo/queryKeys";
import SessionOverview from "routes/swoogo/SessionOverview";
import useRecordTableSections from "routes/swoogo/useRecordTableSections";
import SwoogoService from "services/Swoogo";

const NOT_FOUND_STATUS = 404;

const SessionDetail = () => {
  const { id } = useParams();
  const tabs = useAppTabs({
    basePath: `/swoogo/sessions/${id}`,
    defaultTab: "overview",
  });
  const sessionId = Number(id);

  const canRead = useAuthorized([ACL_RELATIONSHIPS.swoogo.readSession]);
  const tableSections = useRecordTableSections(
    ["registrants", "attendances"],
    "session_id",
    sessionId
  );

  const {
    data: session,
    isLoading,
    error,
  } = useQuery({
    queryKey: swoogoSessionKeys.detail(sessionId),
    queryFn: () => SwoogoService.getSession(sessionId),
    enabled: canRead,
    retry: false,
  });

  const title = session?.name || `Session #${id}`;
  usePageTitle(title);

  if (!canRead) {
    return (
      <AccessDenied description="You don't have permission to view this session." />
    );
  }

  if (error?.response?.status === NOT_FOUND_STATUS) {
    return <NotFoundItem />;
  }

  if (error) {
    return <RecordLoadError title="Could not load the session" />;
  }

  const sections = [
    {
      key: "overview",
      label: "Overview",
      icon: Presentation,
      content: <SessionOverview session={session} isLoading={isLoading} />,
    },
    ...tableSections,
  ];

  return (
    <SidebarDetailLayout
      sections={sections}
      tabs={tabs}
      header="Session"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/swoogo/sessions"
          parentLabel="Sessions"
          current={title}
        />
      }
      title={title}
    />
  );
};

export default SessionDetail;
